// src/pages/admin/billing/AdminBillingContractsPage.js
import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminData } from '../../../contexts/AdminDataContext';
import pb from '../../../lib/pocketbase';
import PDFGenerator from '../../../utils/PDFGenerator';
import ContractForm from '../../../components/admin/billing/contracts/ContractForm';

function AdminBillingContractsPage() {
    const { customers: ctxCustomers, projects: ctxProjects, quotes: ctxQuotes } = useAdminData() || {};
    const [contracts, setContracts] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');
    const [showCreateModal, setShowCreateModal] = React.useState(false);
    const [editingContract, setEditingContract] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    const [showContractsGeneralQR, setShowContractsGeneralQR] = React.useState(false);

    // PDF Generation Settings
    const [settings, setSettings] = React.useState({});

    // Context data
    const [customers, setCustomers] = React.useState([]);
    const [projects, setProjects] = React.useState([]);
    const [quotes, setQuotes] = React.useState([]);

    React.useEffect(() => {
        if (Array.isArray(ctxCustomers)) setCustomers(ctxCustomers);
        if (Array.isArray(ctxProjects)) setProjects(ctxProjects);
        if (Array.isArray(ctxQuotes)) setQuotes(ctxQuotes);
    }, [ctxCustomers, ctxProjects, ctxQuotes]);

    const fetchContracts = async () => {
        setLoading(true);
        try {
            const res = await pb.collection('contracts').getList(1, 200, {
                sort: '-created',
                expand: 'customer,project,quote'
            });
            setContracts(res.items || []);
        } catch (e) {
            console.error("Error contracts:", e);
            setContracts([]);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchContracts();
        // Load Settings for PDF
        (async () => {
            try {
                const res = await pb.collection('settings').getList(1, 1, { sort: '-created' });
                const s = res?.items?.[0];
                if (s) { setSettings({ ...s, _baseUrl: pb?.baseUrl }); }
            } catch { }
        })();
    }, []);

    const buildContractsHeaderShareText = (accountUrl) => {
        return [
            'Bonjour,',
            '',
            'Retrouvez vos contrats dans votre espace client :',
            accountUrl,
            '',
            'Bien à vous,',
            'L’équipe INFOCOLAB'
        ].join('\n');
    };



    const handleSave = async (data) => {
        setSaving(true);
        try {
            // Clean payload
            const payload = { ...data };
            if (!payload.project) payload.project = null;
            if (!payload.quote) payload.quote = null;

            // Note: If schema is broken (expects User), this will fail. 
            // We catch the error and suggest the fix.

            if (editingContract) {
                await pb.collection('contracts').update(editingContract.id, payload);
            } else {
                await pb.collection('contracts').create(payload);
            }
            setShowCreateModal(false);
            setEditingContract(null);
            fetchContracts();
        } catch (e) {
            console.error(e);
            let msg = "Erreur lors de l'enregistrement.";
            if (e.message?.includes('relation')) {
                msg += "\n\nLa base de données semble mal configurée pour les clients. Cliquez sur l'icône 🛠️ en haut à droite pour réparer.";
            }
            alert(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Supprimer ce contrat ?")) return;
        try {
            await pb.collection('contracts').delete(id);
            fetchContracts();
        } catch (e) {
            alert("Erreur suppression");
        }
    };

    const openContract = (contract) => {
        const w = window.open('', '_blank');
        if (w) {
            const signatureHtml = contract.signature
                ? `<div style="margin-top:60px; padding:30px; border:2px dashed #d4af37; border-radius:12px; background:#fffaf0; display:inline-block;">
                     <p style="margin:0 0 10px 0; color:#d4af37; font-weight:bold; font-size:0.8rem; text-transform:uppercase; letter-spacing:1px;">Signature Électronique Certifiée</p>
                     <img src="${contract.signature}" alt="Signature" style="max-height:100px; display:block;" />
                     <p style="margin:10px 0 0 0; color:#64748b; font-size:0.75rem;">Signé le: ${contract.signed_at ? new Date(contract.signed_at).toLocaleString() : 'Date inconnue'}</p>
                   </div>`
                : `<div style="margin-top:40px; border:1px dashed #ccc; padding:20px; color: #94a3b8; border-radius:8px;">
                     <p><em>Document non signé à ce jour</em></p>
                   </div>`;

            w.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>${contract.title || 'Contrat'}</title>
                <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap" rel="stylesheet">
                <style>
                    body { font-family: 'Inter', sans-serif; line-height: 1.6; color: #1e293b; max-width: 800px; margin: 40px auto; padding: 40px; background: #f8fafc; }
                    .paper { background: white; padding: 60px; box-shadow: 0 10px 30px rgba(0,0,0,0.05); border-radius: 4px; min-height: 1000px; }
                    h1, h2, h3 { color: #0f172a; }
                    hr { border: 0; border-top: 1px solid #e2e8f0; margin: 30px 0; }
                </style>
            </head>
            <body>
                <div class="paper">
                    ${contract.content}
                    <hr />
                    ${signatureHtml}
                </div>
            </body>
            </html>
        `);
            w.document.close();
        }
    };

    const handleCreateCustomer = async (data) => pb.collection('customers').create(data);
    const handleCreateVehicle = async (data) => pb.collection('projects').create(data);

    // Helpers for display
    const getCustomerName = (c) => c.expand?.customer?.name || (customers.find(u => u.id === c.customer)?.name) || '—';
    const getProjectName = (c) => c.expand?.project?.name || (projects.find(p => p.id === c.project)?.name) || '—';
    const statusLabel = (s) => ({ draft: 'Brouillon', sent: 'Envoyé', signed: 'Signé', rejected: 'Refusé' })[s] || s;

    const filtered = contracts.filter(c => {
        const t = search.toLowerCase();
        return (c.title?.toLowerCase().includes(t)) || (getCustomerName(c).toLowerCase().includes(t));
    });

    const handleContractPDF = async (contract, mode = 'preview') => {
        try {
            // Expand relations if needed
            let fullContract = { ...contract };
            if (!fullContract.expand?.customer && fullContract.customer) {
                try { fullContract.expand = { ...fullContract.expand, customer: await pb.collection('customers').getOne(fullContract.customer) }; } catch { }
            }
            if (!fullContract.expand?.project && fullContract.project) {
                try { fullContract.expand = { ...fullContract.expand, project: await pb.collection('projects').getOne(fullContract.project) }; } catch { }
            }

            const generator = new PDFGenerator();
            await generator.generateContractPDF(fullContract, settings);

            if (mode === 'download') {
                generator.download(`Contrat_${contract.title || contract.id}.pdf`);
            } else {
                generator.preview();
            }
        } catch (e) {
            console.error('PDF Contract Error:', e);
            alert("Erreur lors de la génération du PDF");
        }
    };

    return (
        <AdminLayout title="Contrats">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ marginTop: 0 }}>Contrats</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                    <button
                        className="btn-primary"
                        onClick={() => { setEditingContract(null); setShowCreateModal(true); }}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 10,
                            padding: '10px 14px', borderRadius: 10,
                            background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                            border: '1px solid rgba(79,70,229,0.6)',
                            boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                            color: 'white'
                        }}
                    >
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                        </span>
                        <span>Nouveau contrat</span>
                    </button>
                </div>
            </div>
            <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .customer-avatar { width:36px; height:36px; border-radius:999px; display:grid; place-items:center; font-weight:700; background:#3b82f6; color:#fff; }
        .header-shape{ border-radius:10px; padding:8px 10px; }
        .btn-accent{ background: rgba(79,70,229,0.12); color: var(--admin-accent); border:1px solid rgba(79,70,229,0.45); }
        .btn-accent:hover{ background: rgba(79,70,229,0.22) !important; border-color: rgba(79,70,229,0.6) !important; color: var(--admin-accent) !important; }
        .btn-danger{ background: rgba(239,68,68,0.12); color:#ef4444; border:1px solid rgba(239,68,68,0.45); }
        .btn-danger:hover{ background: rgba(239,68,68,0.22) !important; border-color: rgba(239,68,68,0.6) !important; color:#ef4444 !important; }
        .has-tip{ position:relative; }
        .has-tip::after{ content: attr(data-tip); position:absolute; bottom: calc(100% + 8px); left:50%; transform: translateX(-50%); background: var(--admin-panel); color: var(--admin-text); border:1px solid var(--admin-border); border-radius:8px; padding:6px 8px; font-size:.8rem; white-space:nowrap; box-shadow: 0 8px 18px rgba(0,0,0,0.18); opacity:0; pointer-events:none; transition: opacity .15s ease; z-index: 70; }
        .has-tip::before{ content:""; position:absolute; bottom: calc(100% + 4px); left:50%; transform: translateX(-50%) rotate(45deg); width:10px; height:10px; background: var(--admin-panel); border-left:1px solid var(--admin-border); border-top:1px solid var(--admin-border); opacity:0; transition: opacity .15s ease; z-index: 70; }
        .has-tip:hover::after, .has-tip:hover::before{ opacity:1; }
        .searchbar{ position:relative; display:flex; align-items:center; gap:8px; }
        .searchbar .icon{ position:absolute; left:10px; color: var(--admin-muted); }
        .searchbar input{ width:260px; background: var(--admin-panel); border:1px solid var(--admin-border); color: var(--admin-text); border-radius:10px; padding:8px 10px 8px 30px; outline:none; transition: box-shadow .15s ease, border-color .15s ease; }
        .searchbar input::placeholder{ color: var(--admin-muted); }
        .searchbar input:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        /* Group container neutral; each button framed */
        .header-actions-frame{ display:flex; gap:8px; padding:0; border:none; background: transparent; }
        .header-actions-frame .header-pill{ border:1px solid var(--admin-border); background: var(--admin-panel-2); }
        .admin-shell.admin-light .header-actions-frame .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-header h3 { margin: 0; font-size: 1.1rem; }
        .modal-body { padding: 16px 20px; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        @media (max-width: 720px) {
          .invoices-header{ flex-wrap: wrap; }
          .invoices-search{ width:100%; margin-left:0 !important; }
          .invoices-search .searchbar{ width:100%; }
          .invoices-search .searchbar input{ width:100%; }
        }
      `}</style>

            <div className="card" style={{ marginTop: 12 }}>
                <div className="invoices-header" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div className="header-actions-frame">
                        <button className="header-pill header-shape has-tip" data-tip="QR Espace client" title="QR Espace client" aria-label="QR Espace client" onClick={() => setShowContractsGeneralQR(true)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z" />
                            </svg>
                        </button>
                        <button className="header-pill header-shape has-tip" data-tip="Copier le lien" onClick={async () => {
                            const link = window.location.origin + '/admin/facturation/contrats';
                            try { await navigator.clipboard.writeText(link); alert('Lien copié'); } catch { }
                        }}>Copier le lien</button>
                        <button className="header-pill header-shape has-tip" data-tip="Copier le message" onClick={async () => {
                            const origin = window.location.origin; const accountUrl = `${origin}/mon-compte`;
                            const shareText = buildContractsHeaderShareText(accountUrl);
                            try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { }
                        }}>Copier le message</button>
                        <button className="header-pill header-shape has-tip" data-tip="Partager" onClick={async () => {
                            const origin = window.location.origin; const accountUrl = `${origin}/mon-compte`;
                            const shareText = buildContractsHeaderShareText(accountUrl);
                            if (navigator.share) { try { await navigator.share({ title: 'Espace client - Contrats', text: shareText, url: accountUrl }); } catch { } }
                            else { try { await navigator.clipboard.writeText(shareText); alert('Texte copié'); } catch { alert('Partage non supporté'); } }
                        }}>Partager</button>
                    </div>
                    <div className="searchbar invoices-search" style={{ marginLeft: 'auto' }}>
                        <svg className="icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
                        <input
                            type="text"
                            placeholder="Rechercher un contrat..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginTop: 12 }}>
                <div className="quotes-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
                    {filtered.map(contract => (
                        <div key={contract.id} style={{ border: '1px solid var(--admin-border)', borderRadius: 12, padding: 12, background: 'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div className="customer-avatar">
                                    {(getCustomerName(contract) || '—').charAt(0).toUpperCase()}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{contract.title || 'Contrat'}</div>
                                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getCustomerName(contract)}</div>
                                </div>
                                <div style={{ marginLeft: 'auto' }}>
                                    <span style={{ padding: '4px 8px', borderRadius: 999, border: '1px solid var(--admin-border)', fontSize: '.8rem' }}>{statusLabel(contract.status)}</span>
                                </div>
                            </div>

                            <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h18v18H3z" /></svg>
                                    <span>Projet: {getProjectName(contract)}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 14H7v-2h10v2zm0-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg>
                                    <span>Signé le: {contract.signed_at ? new Date(contract.signed_at).toLocaleDateString() : 'Non signé'}</span>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap', position: 'relative' }}>
                                <button
                                    className="header-pill header-shape btn-muted has-tip"
                                    data-tip="Aperçu Web"
                                    title="Aperçu Web"
                                    aria-label="Aperçu Web"
                                    onClick={() => openContract(contract)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" /></svg>
                                </button>
                                <button
                                    className="header-pill header-shape btn-muted has-tip"
                                    data-tip="Aperçu PDF"
                                    title="Aperçu PDF"
                                    aria-label="Aperçu PDF"
                                    onClick={() => handleContractPDF(contract, 'preview')}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" /></svg>
                                </button>
                                <button
                                    className="header-pill header-shape btn-muted has-tip"
                                    data-tip="Télécharger PDF"
                                    title="Télécharger PDF"
                                    aria-label="Télécharger PDF"
                                    onClick={() => handleContractPDF(contract, 'download')}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
                                </button>
                                <button
                                    className="header-pill header-shape btn-accent has-tip"
                                    data-tip="Modifier le contrat"
                                    title="Modifier"
                                    aria-label="Modifier"
                                    onClick={() => { setEditingContract(contract); setShowCreateModal(true); }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                                </button>
                                <button
                                    className="header-pill header-shape btn-danger has-tip"
                                    data-tip="Supprimer le contrat"
                                    title="Supprimer"
                                    aria-label="Supprimer"
                                    onClick={() => handleDelete(contract.id)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                                </button>
                            </div>
                            <div style={{ textAlign: 'center', color: 'var(--admin-muted)', fontSize: '.85rem', marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--admin-border)' }}>Créé le {new Date(contract.created).toLocaleDateString()}</div>
                        </div>
                    ))}

                    {filtered.length === 0 && !loading && (
                        <div style={{ gridColumn: '1 / -1', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, width: '100%' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: .7, display: 'block' }}>
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                </svg>
                                <h3 style={{ margin: 0 }}>Aucun contrat trouvé</h3>
                                <p style={{ margin: 0, color: 'var(--admin-muted)' }}>
                                    {search ? `Aucun résultat pour "${search}"` : 'Commencez par créer votre premier contrat'}
                                </p>
                                {!search && (
                                    <button
                                        className="btn-primary"
                                        onClick={() => { setEditingContract(null); setShowCreateModal(true); }}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 8,
                                            padding: '10px 14px', borderRadius: 10,
                                            background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                                            border: '1px solid rgba(79,70,229,0.6)',
                                            boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                                            color: 'white'
                                        }}
                                    >
                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                            </svg>
                                        </span>
                                        <span>Nouveau contrat</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showCreateModal && (
                <ContractForm
                    contract={editingContract}
                    customers={customers}
                    projects={projects}
                    quotes={quotes}
                    onSubmit={handleSave}
                    onCancel={() => { setShowCreateModal(false); setEditingContract(null); }}
                    loading={saving}
                    onCreateCustomer={handleCreateCustomer}
                    onCreateVehicle={handleCreateVehicle}
                />
            )}

            {showContractsGeneralQR && (
                <div className="modal-overlay" onMouseDown={(e) => { if (e.target.classList.contains('modal-overlay')) setShowContractsGeneralQR(false); }}>
                    <div className="modal-content" style={{ maxWidth: 520 }}>
                        <div className="modal-header">
                            <h3>QR code – Espace client</h3>
                            <button className="close-btn" onClick={() => setShowContractsGeneralQR(false)} aria-label="Fermer">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                            </button>
                        </div>
                        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
                            {(() => {
                                const url = `${window.location.origin}/mon-compte`;
                                const qr = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(url)}`;
                                return (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            <img src={qr} alt="QR Espace client" width={240} height={240} style={{ borderRadius: 12, border: '1px solid var(--admin-border)', background: 'white' }} />
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <button className="header-pill header-shape btn-accent" onClick={async () => { try { await navigator.clipboard.writeText(url); alert('Lien copié'); } catch { } }}>Copier le lien</button>
                                            <button className="header-pill header-shape" onClick={() => { try { const a = document.createElement('a'); a.href = qr; a.download = 'qr-espace-client.png'; document.body.appendChild(a); a.click(); a.remove(); } catch { } }}>Télécharger QR</button>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

export default AdminBillingContractsPage;
